/**
 * /de/prd-anforderungsdokument — PM concept page (DE, single-locale).
 *
 * Targets prd abkürzung (150) / was ist ein prd / product requirements
 * document deutsch. Concept + the PRD-drift reframe.
 */
import { MarketingShell } from './MarketingShell';

export function DePrd() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/prd-anforderungsdokument"
      hreflangLocales={['de']}
      documentTitle="PRD: Was die Abkürzung bedeutet (+ Vorlage) · Draftlize"
      documentDescription="PRD steht für Product Requirements Document. Was reingehört, eine Vorlage zum Kopieren — und wie du verhinderst, dass es nach jedem Sprint veraltet."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Leitfaden · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 58px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            PRD – das Product<br />
            <em>Requirements Document, erklärt.</em>
          </h1>
          <p className="lede">
            <strong>PRD</strong> steht für <em>Product Requirements Document</em> – das Anforderungsdokument, das festhält, <em>was</em> gebaut werden soll und <em>warum</em>. Es bündelt Problem, Umfang, Anforderungen und die Entscheidungen dahinter, damit Team und Stakeholder dasselbe Bild haben. Der wunde Punkt: Nach zwei, drei Sprints stimmt das PRD nicht mehr mit der Realität überein – es driftet. Hier: Aufbau, eine Vorlage, und wie du PRD-Drift verhinderst.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Aufbau eines PRD, das trägt.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Problem & Ziel</strong> – wessen Problem, warum jetzt, welches Outcome.</li>
            <li><strong>Umfang (in/out)</strong> – was bewusst nicht dazugehört.</li>
            <li><strong>Anforderungen</strong> – funktional und nicht-funktional, priorisiert.</li>
            <li><strong>Entscheidungen & Trade-offs</strong> – was abgewogen und verworfen wurde.</li>
            <li><strong>Offene Fragen & Erfolgskriterien</strong>.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Diese Vorlage kannst du direkt kopieren. Der wertvollste – und am schnellsten veraltende – Teil ist Punkt 4: die Entscheidungen. Ein PRD ist nur das sichtbare Ende der <a href="/de/produkt-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Roadmap</a> und der Entscheidungen darunter.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Das eigentliche Problem: <em>PRD-Drift.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Ein PRD ist am Tag der Freigabe korrekt. Dann ändert ein Meeting eine Annahme, ein Edge-Case kippt eine Entscheidung – und das Dokument bleibt stehen. Niemand pflegt ein 12-Seiten-Dokument von Hand nach. So baut ein KI-Agent Wochen später auf einer Spec, die nicht mehr gilt.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Anforderungen werden Karten</h3>
            <p>Jede Anforderung und jede Entscheidung wird eine adressierbare Karte statt einer Zeile auf Seite sieben – mit Begründung und Owner, per ID auffindbar.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Ändert sich eine Entscheidung, flaggt das PRD</h3>
            <p>Kippt eine spätere Entscheidung eine frühere, werden die abhängigen Karten – und damit der betroffene PRD-Teil – automatisch „stale“. Das Dokument lügt nicht mehr drei Wochen lang still.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Dein KI-Agent liest die gültige Spec</h3>
            <p>Claude Code oder Cursor lesen das PRD über MCP – als Kontext, der aktuell ist oder sichtbar markiert, nicht als statisches Dokument, dessen Stand niemand kennt.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Ein PRD ist am Freigabetag korrekt. Das Problem sind die sechs Wochen danach, in denen es niemand nachzieht.
        </blockquote>
        <cite>Mach Anforderungen zu Karten – dann meldet das PRD selbst, wenn es veraltet.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Wofür steht PRD?', a: 'Für Product Requirements Document – auf Deutsch das Produkt-Anforderungsdokument. Es beschreibt, was gebaut werden soll und warum.' },
            { q: 'PRD vs. Lastenheft – ist das dasselbe?', a: 'Verwandt, aber nicht gleich. Das klassische Lastenheft ist oft starr und vertraglich; ein PRD ist leichter, outcome-orientiert und lebt mit dem Produkt. Genau deshalb muss es aktuell bleiben.' },
            { q: 'Wer schreibt das PRD?', a: 'Meist das Produktmanagement, gemeinsam mit Engineering und Design. Entscheidend ist, dass die Entscheidungen darin auffindbar und nachvollziehbar bleiben.' },
            { q: 'Wie halte ich das PRD aktuell?', a: 'Indem Anforderungen und Entscheidungen verknüpfte Karten werden statt eingefrorener Absätze. Ändert sich eine Entscheidung, flaggt Draftlize die abhängigen Stellen automatisch als „stale“.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Ein PRD, das nicht driftet <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Schreib dein PRD als verknüpfte Karten. Jede Anforderung wird adressierbar, und eine geänderte Entscheidung markiert die betroffenen Stellen automatisch – Schluss mit PRD-Drift.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
